import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHero, { Breadcrumb } from "@/components/feature/PageHero";
import NewsCard from "@/components/feature/NewsCard";
import { useSeo } from "@/hooks/useSeo";
import { newsCategories, newsItems, type NewsCategory } from "@/mocks/news";

const PAGE_SIZE = 5;

export default function News() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const categoryParam = params.get("cat") ?? "全部";
  const activeCategory = (newsCategories.includes(categoryParam as NewsCategory | "全部")
    ? categoryParam
    : "全部") as NewsCategory | "全部";
  const [keyword, setKeyword] = useState(params.get("q") ?? "");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useSeo({
    title: "星光传媒影视资讯",
    description:
      "星光传媒影视资讯频道，每日更新影视行业动态、剧组幕后花絮与主创独家采访，涵盖电影与剧集的最新消息，助你快速掌握影视圈动态。",
    keywords: "星光传媒,影视资讯,影视新闻,幕后花絮,独家采访,电影资讯,剧集资讯",
    canonicalPath: "/news",
  });

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [activeCategory, keyword]);

  const sorted = useMemo(
    () => [...newsItems].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1)),
    [],
  );

  const filtered = sorted
    .filter((item) => (activeCategory === "全部" ? true : item.category === activeCategory))
    .filter((item) => {
      const text = keyword.trim();
      if (!text) return true;
      return (
        item.title.includes(text) ||
        item.summary.includes(text) ||
        item.tags.some((tag) => tag.includes(text))
      );
    });

  const shown = filtered.slice(0, visible);
  const hero = sorted[0];

  const switchCategory = (next: NewsCategory | "全部") => {
    const nextParams = new URLSearchParams(params);
    if (next === "全部") {
      nextParams.delete("cat");
    } else {
      nextParams.set("cat", next);
    }
    nextParams.delete("q");
    setKeyword("");
    setParams(nextParams, { replace: true });
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (value.length > 0) {
      navigate(`/search?q=${encodeURIComponent(value)}&scope=news`);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar currentLabel="资讯" />
      <main>
        <PageHero
          eyebrow="Newsroom"
          title="星光传媒影视资讯"
          description="行业动态、剧组花絮与主创独家采访。我们把银幕之外发生的事也认真记录下来，每天更新，随时可读。"
          background="https://readdy.ai/api/search-image?query=Cinematic%20moody%20newsroom%20desk%20with%20film%20reels%2C%20notes%20and%20a%20warm%20desk%20lamp%20against%20a%20deep%20navy%20background%2C%20atmospheric%20editorial%20photography%2C%20high%20detail&width=1600&height=900&seq=news-hero-bg&orientation=landscape"
        />

        <div className="px-4 md:px-6 py-5 border-b border-background-200">
          <div className="max-w-[1440px] mx-auto">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />
          </div>
        </div>

        <section className="px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-[1440px] mx-auto">
            {hero && (
              <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4 md:gap-5 mb-8 md:mb-10">
                <NewsCard item={hero} variant="feature" />
                <div className="bg-background-100 rounded-lg p-4 md:p-6 flex flex-col justify-between">
                  <div>
                    <p className="text-xs tracking-[0.22em] uppercase text-secondary-600 mb-2">今日头条</p>
                    <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 leading-snug mb-2">
                      {hero.title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{hero.summary}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      {hero.publishedAt}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      {(hero.views / 10000).toFixed(1)} 万阅读
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-900">{hero.category}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 md:gap-4 mb-5">
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 overflow-x-auto no-scrollbar">
                {newsCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => switchCategory(category)}
                    className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      activeCategory === category
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {category === "全部" ? "全部资讯" : category}
                  </button>
                ))}
              </div>
              <form onSubmit={handleSearch} className="flex items-center gap-2 w-full lg:w-auto">
                <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-background-200 bg-background-100 focus-within:border-primary-300 flex-1 lg:w-64">
                  <i className="ri-search-line text-sm text-foreground-400 w-4 h-4 flex items-center justify-center"></i>
                  <input
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="搜索资讯关键词"
                    aria-label="搜索资讯关键词"
                    className="flex-1 min-w-0 bg-transparent text-sm outline-none text-foreground-900 placeholder:text-foreground-400"
                  />
                  {keyword && (
                    <button
                      type="button"
                      onClick={() => setKeyword("")}
                      aria-label="清除关键词"
                      className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
                    >
                      <i className="ri-close-circle-line text-base w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                >
                  站内搜索
                </button>
              </form>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8">
              <div>
                <p className="text-sm text-foreground-500 mb-2">
                  {activeCategory === "全部" ? "全部栏目" : activeCategory} · 共 {filtered.length} 条
                  <span className="text-foreground-400">（演示数据）</span>
                </p>
                {shown.length > 0 ? (
                  <>
                    <div>
                      {shown.map((item) => (
                        <NewsCard key={item.id} item={item} variant="horizontal" />
                      ))}
                    </div>
                    <div className="mt-6 flex flex-col items-center gap-3">
                      {visible < filtered.length ? (
                        <button
                          type="button"
                          onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                          className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          加载更多资讯
                          <i className="ri-arrow-down-line w-4 h-4 flex items-center justify-center"></i>
                        </button>
                      ) : (
                        <p className="text-xs text-foreground-400">已显示全部 {filtered.length} 条资讯</p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="py-16 text-center bg-background-100 rounded-lg">
                    <span className="inline-flex w-14 h-14 rounded-full bg-background-50 text-foreground-400 items-center justify-center mb-4">
                      <i className="ri-newspaper-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                    </span>
                    <h3 className="font-heading text-lg text-foreground-950 mb-2">暂无符合条件的资讯</h3>
                    <p className="text-sm text-foreground-500 mb-5">换个关键词，或者看看其它栏目。</p>
                    <button
                      type="button"
                      onClick={() => {
                        switchCategory("全部");
                        setKeyword("");
                      }}
                      className="h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                    >
                      查看全部资讯
                    </button>
                  </div>
                )}
              </div>

              <aside className="space-y-6">
                <div className="bg-background-100 rounded-lg p-4 md:p-5">
                  <h2 className="text-sm font-medium text-foreground-950 mb-3 flex items-center gap-1.5">
                    <i className="ri-fire-line text-accent-600 w-4 h-4 flex items-center justify-center"></i>
                    热门资讯
                  </h2>
                  <ol className="space-y-3">
                    {[...newsItems]
                      .sort((a, b) => b.views - a.views)
                      .slice(0, 5)
                      .map((item, index) => (
                        <li key={item.id}>
                          <Link
                            to={`/news/detail/${item.id}`}
                            className="flex gap-2.5 group cursor-pointer"
                          >
                            <span
                              className={`font-heading text-sm w-4 shrink-0 ${
                                index < 3 ? "text-accent-600" : "text-foreground-400"
                              }`}
                            >
                              {String(index + 1).padStart(2, "0")}
                            </span>
                            <span className="text-xs text-foreground-700 leading-relaxed line-clamp-2 group-hover:text-primary-700 transition-colors">
                              {item.title}
                            </span>
                          </Link>
                        </li>
                      ))}
                  </ol>
                </div>

                <div className="bg-secondary-100 rounded-lg p-4 md:p-5">
                  <h2 className="text-sm font-medium text-secondary-900 mb-2">关于资讯频道</h2>
                  <p className="text-xs text-secondary-900/75 leading-relaxed">
                    资讯内容由星光编辑部整理撰写，包括行业新闻、剧组花絮与独家采访三类栏目。所有报道均为虚构演示内容。
                  </p>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}